import { Link } from "@tanstack/react-router";
import {
  ChartBar,
  Package,
  TrendingDown,
  TrendingUp,
  Users,
} from "@/components/icons";
import { Skeleton } from "@/components/kit";
import { cn } from "@/lib/utils";
import {
  useAgingStockQuery,
  useDeadStockQuery,
  useDeadStockSettingsQuery,
  useProfitSummaryQuery,
  useSalesSummaryQuery,
  useShippingPerformanceQuery,
  useStockValuationQuery,
  useCustomerReportSummaryQuery,
} from "@/modules/reports/presentation/api/queries";
import { buildDateRangePreset } from "@/modules/reports/presentation/lib/date-range";
import { formatLak } from "@/shared/lib/currency";

function OverviewCard({
  href,
  icon: Icon,
  label,
  value,
  sub,
  accentClass,
  valueClass,
  className,
}: {
  href: string;
  icon: typeof TrendingUp;
  label: string;
  value: string;
  sub?: string;
  accentClass: string;
  valueClass?: string;
  className?: string;
}) {
  return (
    <Link
      to={href}
      className={cn(
        "brand-card flex h-full items-center gap-3 p-3 transition-colors hover:bg-muted/40 sm:p-4",
        className,
      )}
    >
      <div
        className={`flex size-10 shrink-0 items-center justify-center rounded-xl ${accentClass}`}
      >
        <Icon />
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-muted-foreground text-xs">{label}</span>
        <span
          className={`font-display text-lg tabular-nums leading-tight sm:text-xl ${valueClass ?? ""}`}
        >
          {value}
        </span>
        {sub ? (
          <span className="truncate text-muted-foreground text-xs">{sub}</span>
        ) : null}
      </div>
    </Link>
  );
}

export function DashboardReportsOverview() {
  const week = buildDateRangePreset("7d", "created");
  const profitRange = buildDateRangePreset("7d", "created");

  const sales = useSalesSummaryQuery({
    from: week.from,
    to: week.to,
    basis: "created",
  });
  const profit = useProfitSummaryQuery({
    from: profitRange.from,
    to: profitRange.to,
    basis: "paid_at",
  });
  const valuation = useStockValuationQuery({ limit: 5 });
  const aging = useAgingStockQuery({ minDays: 30, limit: 50 });
  const deadSettings = useDeadStockSettingsQuery();
  const dead = useDeadStockQuery(
    {
      lookbackDays: deadSettings.data?.lookbackDays,
      minDays: deadSettings.data?.minDays,
      limit: 50,
    },
    {
      enabled:
        deadSettings.data?.lookbackDays != null &&
        deadSettings.data?.minDays != null,
    },
  );
  const shipping = useShippingPerformanceQuery({
    from: week.from,
    to: week.to,
    basis: "created",
    limit: 10,
  });
  const customers = useCustomerReportSummaryQuery({
    from: week.from,
    to: week.to,
    basis: "created",
  });

  const loading =
    sales.isLoading ||
    profit.isLoading ||
    valuation.isLoading ||
    shipping.isLoading ||
    customers.isLoading;

  if (loading) {
    return (
      <section className="flex flex-col gap-3">
        <Skeleton className="h-7 w-48 rounded-lg" />
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-6">
          {["a", "b", "c", "d", "e"].map((k) => (
            <Skeleton key={k} className="h-22 rounded-2xl" />
          ))}
        </div>
      </section>
    );
  }

  const revenue = sales.data?.confirmed.revenue ?? "0";
  const orderCount = sales.data?.confirmed.orderCount ?? 0;
  const netProfit = profit.data?.netProfit ?? "0";
  const netMargin = profit.data?.netMarginPercent ?? "0";
  const netNum = Number(netProfit);
  const stockValue = valuation.data?.totalInventoryValueLak ?? "0";
  const agingCount = aging.data?.length ?? 0;
  const deadCount = dead.data?.data.length ?? 0;
  const shipped = shipping.data?.meta.totalShipped ?? 0;
  const returned = shipping.data?.meta.totalReturned ?? 0;
  const returnRate =
    shipped > 0 ? ((returned / shipped) * 100).toFixed(1) : "0";
  const returnNum = Number(returnRate);
  const worst = shipping.data?.data?.[0];
  const activeCustomers = customers.data?.activeCustomers ?? 0;
  const newCustomers = customers.data?.newCustomers ?? 0;
  const returningCustomers = customers.data?.returningCustomers ?? 0;

  const stockSubParts: string[] = [];
  if (agingCount > 0) stockSubParts.push(`ຄ້າງ ${agingCount}`);
  if (deadCount > 0) stockSubParts.push(`ຂາຍຊ້າ ${deadCount}`);
  const stockSub =
    stockSubParts.length > 0 ? stockSubParts.join(" · ") : "ບໍ່ມີລາຍການຕ້ອງສັນໃຈ";

  return (
    <section
      className="flex flex-col gap-3"
      data-tourid="dashboard-reports-overview"
    >
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="font-display text-lg">ພາບລວມຮ້ານ</h2>
          <p className="text-muted-foreground text-sm">
            7 ວັນລ່າສຸດ — ກົດເພື່ອເຂົ້າລາຍງານ
          </p>
        </div>
        <Link
          to="/app/reports?tab=sales&preset=7d"
          className="text-secondary text-sm underline-offset-2 hover:underline"
        >
          ເບິ່ງທັງໝົດ
        </Link>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-6">
        <OverviewCard
          className="xl:col-span-3"
          href="/app/reports?tab=sales&preset=7d&section=overview"
          icon={TrendingUp}
          label="ຍອດຂາຍ (7ວ)"
          value={formatLak(revenue)}
          sub={`${orderCount} ອໍເດີ້`}
          accentClass="bg-primary/20 text-secondary"
        />
        <OverviewCard
          className="xl:col-span-3"
          href="/app/reports?tab=profit&preset=7d&section=overview"
          icon={ChartBar}
          label="ກຳໄລສຸດທິ (7ວ)"
          value={formatLak(netProfit)}
          sub={`ອັດຕາ ${netMargin}%`}
          accentClass="bg-secondary/20 text-secondary"
          valueClass={
            netNum > 0
              ? "text-leaf"
              : netNum < 0
                ? "text-destructive"
                : undefined
          }
        />
        <OverviewCard
          className="xl:col-span-2"
          href={
            agingCount > 0
              ? "/app/reports?tab=stock&section=aging"
              : "/app/reports?tab=stock&section=valuation"
          }
          icon={Package}
          label="ມູນຄ່າຄລັງ"
          value={formatLak(stockValue)}
          sub={stockSub}
          accentClass={
            agingCount > 0 || deadCount > 0
              ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
              : "bg-leaf/15 text-leaf"
          }
        />
        <OverviewCard
          className="xl:col-span-2"
          href="/app/reports?tab=shipping&preset=7d"
          icon={TrendingDown}
          label="ອັດຕາຕີກັບ (7ວ)"
          value={`${returnRate}%`}
          sub={
            worst
              ? `${worst.shippingCarrier} · ${returned}/${shipped}`
              : `${returned}/${shipped} ຕີກັບ`
          }
          accentClass="bg-destructive/10 text-destructive"
          valueClass={
            returnNum >= 15
              ? "text-destructive"
              : returnNum >= 8
                ? "text-amber-700 dark:text-amber-400"
                : undefined
          }
        />
        <OverviewCard
          className="xl:col-span-2"
          href="/app/reports?tab=customers&section=top&preset=7d"
          icon={Users}
          label="Top ລູກຄ້າ (7ວ)"
          value={String(activeCustomers)}
          sub={`ໃໝ່ ${newCustomers} · ຊື້ຊ້ຳ ${returningCustomers}`}
          accentClass="bg-accent/40 text-secondary"
        />
      </div>
    </section>
  );
}
